import { find, forEach, map, reduce, some } from 'lodash/collection';
import { isArray, isBoolean, isEmpty, isEqual } from 'lodash/lang';

import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';

export const CONDITIONAL_LOGIC_FIELD_TYPES = ['radio', 'dropdown', 'dropdownCT', 'multiselect', 'multiselectCT'];

export function getFieldLabel({ updatedQuestion, question, name } = {}) {
  return updatedQuestion || question || name;
}

export function collectConditionalLogic(standardConditionalLogic, allFields, fieldIdKey) {
  const notCustomFieldNameKeyMap = allFields.reduce((accumulatorMap, field) => {
    if (!accumulatorMap.has(field.name) && field.type !== 'CUSTOM') {
      accumulatorMap.set(field.name, field);
    }
    return accumulatorMap;
  }, new Map());

  return reduce(
    allFields,
    function(accumulator, field) {
      const targetFieldId = field[fieldIdKey];
      const targetFieldName = field.name;

      Object.assign(accumulator, getStandardConfigurations(targetFieldId, targetFieldName));

      const configurationList = getConfigurationList(field);

      if (!configurationList) {
        return accumulator;
      }

      forEach(configurationList, function(configuration) {
        const [triggerFieldId, triggerFieldAnswers] = getMatchedConditionFieldDataSafe(
          allFields,
          configuration,
          fieldIdKey
        );

        if (!triggerFieldId) {
          return;
        }

        const isHideCondition = isBoolean(configuration.isHideCondition) ? configuration.isHideCondition : true;

        if (isEmpty(accumulator[triggerFieldId])) {
          accumulator[triggerFieldId] = {
            sourceIdentifier: triggerFieldId,
            required: false,
            conditionalLogicFieldsConfigs: []
          };
        }

        const triggerFieldConditions = accumulator[triggerFieldId];

        const matchedCondition = find(
          triggerFieldConditions.conditionalLogicFieldsConfigs,
          condition => condition.isHideCondition === isHideCondition && isEqual(condition.answers, triggerFieldAnswers)
        );

        if (isEmpty(matchedCondition)) {
          triggerFieldConditions.conditionalLogicFieldsConfigs.push({
            answers: triggerFieldAnswers,
            isHideCondition,
            questions: [targetFieldId]
          });
          return;
        }

        matchedCondition.questions.push(targetFieldId);
      });

      return accumulator;
    },
    {}
  );

  function getStandardConfigurations(fieldId, fieldName) {
    const standardConfiguration = standardConditionalLogic?.[fieldName];
    if (standardConfiguration) {
      const { required, conditionalLogicFieldsConfigs } = standardConfiguration;
      return {
        [fieldId]: {
          sourceIdentifier: fieldId,
          required: required,
          conditionalLogicFieldsConfigs: map(conditionalLogicFieldsConfigs, function(config) {
            return {
              ...config,
              questions: map(config.questions, name => notCustomFieldNameKeyMap.get(name)?.[fieldIdKey])
            };
          })
        }
      };
    }
    return {};
  }
}

function getMatchedConditionFieldDataSafe(allFields, configuration, fieldIdKey) {
  const { conditionField, conditionFieldName, conditionFieldAnswerNames } = configuration;

  const fieldData = extractConditionFieldDataSafe(
    conditionField,
    conditionFieldName,
    conditionFieldAnswerNames,
    'name',
    fieldIdKey
  );
  if (fieldData) {
    return fieldData;
  }

  return getFirstMatchedConditionFieldDataIfExists(
    allFields,
    conditionFieldName,
    conditionFieldAnswerNames,
    'name',
    fieldIdKey
  );
}

function getFirstMatchedConditionFieldDataIfExists(
  allFields,
  conditionFieldName,
  conditionAnswers,
  conditionAnswerKey,
  fieldIdKey
) {
  for (const field of allFields) {
    const fieldData = extractConditionFieldDataSafe(
      field,
      conditionFieldName,
      conditionAnswers,
      conditionAnswerKey,
      fieldIdKey
    );
    if (fieldData) {
      return fieldData;
    }
  }

  return [];
}

function extractConditionFieldDataSafe(field, conditionFieldName, conditionAnswers, conditionAnswerKey, fieldIdKey) {
  const fieldName = field?.name;
  const fieldId = field?.[fieldIdKey];
  const codeDefinitionList = field?.codeDefinationList;

  if (!fieldId || fieldName !== conditionFieldName || !codeDefinitionList) {
    return;
  }

  try {
    return [
      fieldId,
      map(conditionAnswers, function(answer) {
        return find(codeDefinitionList, [conditionAnswerKey, answer]).decode;
      })
    ];
  } catch (e) {}
}

function getConfigurationList(field) {
  const configurationResponse = field?.fieldConfigurationResponse;

  if (isEmpty(configurationResponse)) {
    return null;
  }

  return isArray(configurationResponse) ? configurationResponse : [configurationResponse];
}

export function optionUsedInConditionalLogicModalConfirmation(affectedFieldLabels, optionName) {
  return ModalBoxes.confirm({
    title: 'Option Used in Conditional Logic',
    content: (
      <>
        <p>
          You are attempting to delete the option <b>"{optionName}"</b> from this field's options list.
        </p>
        <p>This option is currently used in the conditional logic for the following form fields:</p>
        <ul>
          {affectedFieldLabels.map(name => (
            <li>{name}</li>
          ))}
        </ul>
        <p>
          If you confirm the deletion, the option <b>"{optionName}"</b> will be automatically removed from the
          conditional logic for all the listed fields. This action may change the display logic or overall behavior of
          your form.
        </p>
        <p>Are you sure you want to proceed?</p>
      </>
    )
  });
}

export function confirmWhenConditionalLogicAffected({ message, isAffects }) {
  if (isAffects) {
    return ModalBoxes.confirm({
      title: 'Conditional Logic Configuration',
      content: message,
      confirmButton: 'Yes',
      cancelButton: 'No'
    }).then(() => ({ isAffects }));
  }
  return Promise.resolve({ isAffects });
}

export function isUsedInConditionalLogic(allFields, inputType, fieldName) {
  if (!CONDITIONAL_LOGIC_FIELD_TYPES.includes(inputType)) {
    return false;
  }
  return allFields.reduce(function(accumulator, field) {
    if (some(field.fieldConfigurationResponse, ['conditionFieldName', fieldName])) {
      accumulator = true;
    }
    return accumulator;
  }, false);
}

export function someFieldsHasConditionalLogic(fields) {
  return some(fields, field => !isEmpty(field.fieldConfigurationResponse) || !isEmpty(field.hideAllConfiguration));
}
